/**
 * 数据调用记录（AUDIT LOG）。
 *
 * 这一屏的立场：不留痕的授权等于没有授权。
 *   - 每条记录来自后端哈希链审计表，页面只做呈现，不做美化；
 *   - 顶部如实给出链路完整性结论（链断了必须看得见，且标出断点）；
 *   - 记录可展开查看完整哈希与前序哈希，便于举证比对。
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { AuditIntegrity, ConsentAuditEvent } from '@hew3/shared';
import { consentScopes } from '@hew3/shared';
import { api } from '../../lib/api.ts';
import { formatDateTime } from '../../lib/format.ts';
import { Badge, Button, Card, StateBox } from '../../components/ui.tsx';

interface AuditResponse {
  items: ConsentAuditEvent[];
}

const actionLabels: Record<ConsentAuditEvent['action'], string> = {
  read: '读取',
  grant: '开启授权',
  revoke: '撤回授权',
  login: '登录',
  update: '修改',
  create: '创建',
};

const actionTone: Record<ConsentAuditEvent['action'], 'h' | 'e' | 'w' | 'ok' | 'warn' | 'danger' | 'ghost'> = {
  read: 'e',
  grant: 'ok',
  revoke: 'warn',
  login: 'ghost',
  update: 'h',
  create: 'h',
};

const actorLabels: Record<ConsentAuditEvent['actorType'], string> = {
  member: '本人',
  guardian: '管家',
  expert: '专家',
  system: '系统',
  // 平台内部员工（后台操作）。姓名与角色在 actorName 里，
  // actorId 是工号 —— 让成员能追溯到具体是哪位员工。
  staff: '平台员工',
  // 外部合作机构（服务方工作台）。机构名与联系人在 actorName 里，
  // actorId 是服务方账号 id —— 与「服务方授权」页的读取记录同一口径。
  provider: '服务方',
};

const scopeLabels: Record<string, string> = {
  account: '账号',
  ...Object.fromEntries(consentScopes.map((s) => [s.key, s.label])),
};

function scopeText(scope: ConsentAuditEvent['scope']): string {
  if (!scope) return '—';
  return scopeLabels[scope] ?? scope;
}

export function AuditPage() {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState<string | null>(null);

  const auditQuery = useQuery({
    queryKey: ['consent', 'audit'],
    queryFn: () => api.get<AuditResponse>('/api/consent/audit?limit=100'),
  });

  const integrityQuery = useQuery({
    queryKey: ['consent', 'audit', 'integrity'],
    queryFn: () => api.get<AuditIntegrity>('/api/consent/audit/integrity'),
  });

  const integrity = integrityQuery.data;
  const items = auditQuery.data?.items ?? [];

  // 首屏加载：骨架屏（三种状态必须都能看见，不能白屏）
  if (auditQuery.isLoading && integrityQuery.isLoading) {
    return (
      <div className="page">
        <div className="scr-head">
          <button type="button" className="back" onClick={() => navigate(-1)} aria-label="返回">
            <svg viewBox="0 0 24 24">
              <path d="M15 5l-7 7 7 7" />
            </svg>
          </button>
          <div className="scr-title">
            数据调用记录<small>AUDIT LOG</small>
          </div>
        </div>
        <div className="skeleton skel-card" />
        <div className="skeleton skel-card" style={{ height: 260 }} />
      </div>
    );
  }

  // 首屏失败：给出整页错误态与重试（不展示残缺记录列表）
  if (auditQuery.isError && !auditQuery.data) {
    return (
      <div className="page">
        <div className="scr-head">
          <button type="button" className="back" onClick={() => navigate(-1)} aria-label="返回">
            <svg viewBox="0 0 24 24">
              <path d="M15 5l-7 7 7 7" />
            </svg>
          </button>
          <div className="scr-title">
            数据调用记录<small>AUDIT LOG</small>
          </div>
        </div>
        <StateBox
          kind="error"
          title="数据调用记录加载失败"
          desc="审计记录是授权可追溯性的依据，加载失败时请重试而不是忽略。"
          action={<Button small onClick={() => void auditQuery.refetch()}>重新加载</Button>}
        />
      </div>
    );
  }

  return (
    <div className="page">
      {/* ---------------- 返回头 ---------------- */}
      <div className="scr-head">
        <button type="button" className="back" onClick={() => navigate(-1)} aria-label="返回">
          <svg viewBox="0 0 24 24">
            <path d="M15 5l-7 7 7 7" />
          </svg>
        </button>
        <div className="scr-title">
          数据调用记录<small>AUDIT LOG</small>
        </div>
      </div>

      {/* ---------------- 完整性结论 ---------------- */}
      <Card tight>
        {integrityQuery.isLoading && <StateBox kind="loading" title="正在校验审计链…" />}
        {integrityQuery.isError && (
          <StateBox
            kind="error"
            title="审计完整性校验失败"
            desc="无法读取链路结论，记录列表仍可查看。"
            action={<Button small onClick={() => void integrityQuery.refetch()}>重新校验</Button>}
          />
        )}
        {integrity && (
          <>
            <div className="row between">
              <span className="text-tiny text-muted">审计链总条数</span>
              <span className="num" style={{ fontSize: 15, fontWeight: 600 }}>
                {integrity.total}
              </span>
            </div>
            <div className="row between" style={{ marginTop: 8 }}>
              <span className="text-tiny text-muted">完整性结论</span>
              {integrity.valid ? (
                <Badge tone="ok">
                  <span style={{ fontSize: 11 }}>链路完整 · 未被篡改</span>
                </Badge>
              ) : (
                <Badge tone="danger">
                  <span style={{ fontSize: 11 }}>链路断裂</span>
                </Badge>
              )}
            </div>
            {!integrity.valid && (
              <div className="note-src" style={{ color: 'var(--danger)', borderLeftColor: 'var(--danger)' }}>
                <b style={{ color: 'var(--danger)' }}>断点记录 ID</b>：{integrity.brokenAt ?? '未知'}
                <br />
                该记录之后的哈希无法验证，平台已进入人工复核流程。
              </div>
            )}
          </>
        )}
        <div className="sec-note">
          这些记录采用哈希链式存储 —— 每条记录的哈希包含前一条的哈希，任何对历史记录的改写都会导致链断裂并在此暴露。
          下次调用时请核对完整哈希。
        </div>
      </Card>

      {/* ---------------- 记录列表 ---------------- */}
      <Card
        title="调用明细"
        more={<span className="card-more">{auditQuery.data ? `最近 ${items.length} 条` : '加载中…'}</span>}
      >
        {auditQuery.isLoading && (
          <div className="page-pad">
            <div className="skeleton skel-card" />
            <div className="skeleton skel-card" />
            <div className="skeleton skel-card" />
          </div>
        )}

        {auditQuery.isError && (
          <StateBox
            kind="error"
            title="调用记录加载失败"
            desc="审计记录是授权可追溯性的依据，加载失败时请重试而不是忽略。"
            action={<Button small onClick={() => void auditQuery.refetch()}>重新加载</Button>}
          />
        )}

        {auditQuery.data && items.length === 0 && (
          <StateBox title="暂无调用记录" desc="授权变更、数据读取与登录都会在这里留痕" />
        )}

        {items.map((event) => {
          const open = expanded === event.id;
          return (
            <div key={event.id} className="list-item" style={{ alignItems: 'flex-start' }}>
              <div className="li-main">
                <div className="row-flex" style={{ gap: 8, flexWrap: 'wrap' }}>
                  <Badge tone={actionTone[event.action]}>{actionLabels[event.action]}</Badge>
                  <span className="li-t">{scopeText(event.scope)}</span>
                </div>
                <div className="li-s">
                  {formatDateTime(event.at)} · {actorLabels[event.actorType]} {event.actorName}
                  {/* 员工操作时显示工号：成员可以据此追溯到具体是哪位员工 */}
                  {event.actorId ? ` · 工号 ${event.actorId}` : ''}
                </div>
                <div className="li-s">
                  目的：{event.purpose}
                  <br />
                  资源：{event.resource}
                  <br />
                  IP：{event.ip}
                </div>
                <div className="row-flex" style={{ gap: 8, marginTop: 6 }}>
                  <span className="num text-tiny text-muted" style={{ letterSpacing: 0.4 }}>
                    hash {event.hash.slice(0, 12)}
                  </span>
                  <button
                    type="button"
                    className="btn sm"
                    onClick={() => setExpanded(open ? null : event.id)}
                    aria-expanded={open}
                  >
                    {open ? '收起哈希' : '展开哈希'}
                  </button>
                </div>
                {open && (
                  <div className="note-src" style={{ wordBreak: 'break-all' }}>
                    <b>本条哈希</b>
                    <br />
                    <span className="num">{event.hash}</span>
                    <br />
                    <b>前序哈希</b>
                    <br />
                    <span className="num">{event.prevHash || '—（链首）'}</span>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </Card>

      <div className="sec-note">
        记录只允许追加、不允许改写。若您发现某条记录与自己的实际操作不符，
        请立即通过「我的 · 隐私与授权」撤回相关授权并联系您的专属管家。
      </div>
    </div>
  );
}
